import type { PdfInsightKind, PdfInsightProvenance } from '../../models/pdfInsight.model';

export type PdfBoardSeedCandidate = {
	insightId: string;
	label: string;
	kind: string;
	level?: number;
	salience: number;
	confidence: number;
	provenance: PdfInsightProvenance;
	evidence?: Array<unknown>;
};

export type PdfBoardSeedNode = {
	pdfAnalysisId?: string;
	pdfInsightId?: string;
	topics?: string[];
};

export const PDF_BOARD_SEED_QUOTAS: ReadonlyArray<readonly [kind: PdfInsightKind, count: number]> =
	[
		['need', 1],
		['outcome', 1],
		['motivation', 1],
		['topic', 3],
		['risk', 1],
		['barrier', 1],
		['stakeholder', 1],
		['opportunity', 1],
	];

export const PDF_BOARD_SEED_LIMIT = PDF_BOARD_SEED_QUOTAS.reduce(
	(total, [, count]) => total + count,
	0
);

const normalizeLabel = (label: string): string =>
	label.trim().toLocaleLowerCase().replace(/\s+/g, ' ');

const candidateScore = (candidate: PdfBoardSeedCandidate): number =>
	candidate.salience * 0.55 +
	candidate.confidence * 0.3 +
	(candidate.evidence?.length ? 0.1 : 0) +
	(candidate.level === 1 ? 0.05 : candidate.level === 2 ? 0.025 : 0);

const rankCandidates = (left: PdfBoardSeedCandidate, right: PdfBoardSeedCandidate): number =>
	candidateScore(right) - candidateScore(left) ||
	left.label.localeCompare(right.label) ||
	left.insightId.localeCompare(right.insightId);

/**
 * Selects the first visible PDF insight ring without making another model call.
 * Kind quotas create variety; ranking favours salient, confident, evidenced and
 * shallower insights. Missing kinds are backfilled with the best remaining
 * insights so sparse and legacy graphs still seed a useful board.
 */
export const selectPdfBoardSeedInsights = <T extends PdfBoardSeedCandidate>(
	insights: T[],
	limit = PDF_BOARD_SEED_LIMIT
): T[] => {
	if (limit <= 0) return [];

	const candidates = insights
		.filter(
			(insight) =>
				insight.insightId &&
				insight.label.trim() &&
				normalizeLabel(insight.label) !== 'document overview'
		)
		.sort(rankCandidates);
	const selected: T[] = [];
	const selectedIds = new Set<string>();
	const selectedLabels = new Set<string>();

	const add = (candidate: T): boolean => {
		const normalizedLabel = normalizeLabel(candidate.label);
		if (selectedIds.has(candidate.insightId) || selectedLabels.has(normalizedLabel)) return false;
		selected.push(candidate);
		selectedIds.add(candidate.insightId);
		selectedLabels.add(normalizedLabel);
		return true;
	};

	for (const [kind, quota] of PDF_BOARD_SEED_QUOTAS) {
		let addedForKind = 0;
		for (const candidate of candidates) {
			if (candidate.kind !== kind || !add(candidate)) continue;
			addedForKind += 1;
			if (addedForKind >= quota || selected.length >= limit) break;
		}
		if (selected.length >= limit) return selected;
	}

	for (const candidate of candidates) {
		add(candidate);
		if (selected.length >= limit) break;
	}

	return selected;
};

/** True only when both the overview and the complete first insight layer exist. */
export const isPdfBoardSeedAlreadyOnBoard = ({
	nodes,
	analysisId,
	insights,
}: {
	nodes: PdfBoardSeedNode[];
	analysisId?: string;
	insights: PdfBoardSeedCandidate[];
}): boolean => {
	if (!analysisId) return false;
	const hasOverview = nodes.some(
		(node) =>
			node.pdfAnalysisId === analysisId &&
			node.topics?.some((topic) => topic.toLowerCase() === 'pdf-role:overview')
	);
	if (!hasOverview) return false;

	const boardInsightIds = new Set(
		nodes
			.filter((node) => node.pdfAnalysisId === analysisId)
			.map((node) => node.pdfInsightId)
			.filter((insightId): insightId is string => Boolean(insightId))
	);
	return selectPdfBoardSeedInsights(insights).every((insight) =>
		boardInsightIds.has(insight.insightId)
	);
};
